import { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown } from '@fortawesome/free-solid-svg-icons';
import {
  MasterProduct,
  AddonsList,
  MasterPackage,
  OrderPackage,
  ProductDraft,
  AddNewProductModalProps,
} from '@helpers/Models';
import { ProductType, WeightType, errorMessage } from '@helpers/Constants';
import { faTrashCan } from '@fortawesome/free-regular-svg-icons';

export const AddNewProductModal: React.FC<AddNewProductModalProps> = ({
  onClose,
  onAdd,
  masterProducts,
  addons,
  masterPackages,
  addedProducts,
}) => {
  const [currentProductDetail, setCurrentProductDetail] =
    useState<MasterProduct | null>(null);
  const [productDraft, setProductDraft] = useState<ProductDraft | null>(null);
  const [selectedPackages, setSelectedPackages] = useState<OrderPackage[]>([]);
  const [searchKey, setSearchKey] = useState('');
  const [openAddons, setOpenAddons] = useState(false);
  const [dropdownOpenIndex, setDropdownOpenIndex] = useState<number | null>(
    null,
  );
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [productSearch, setProductSearch] = useState('');

  const filteredProducts = masterProducts.filter((p) =>
    p.name.toLowerCase().includes(productSearch.toLowerCase()),
  );

  const filteredAddons = addons.filter((a) =>
    a.name.toLowerCase().includes(searchKey.toLowerCase()),
  );

  const remainingPackages = masterPackages.filter(
    (pkg) => !selectedPackages.find((sp) => sp.id === pkg.id),
  );

  /**
   * Handles the selection of a master product and initializes the product drafting state.
   * * This function performs the following operations:
   * 1. Sets the current product detail view.
   * 2. Initializes a new product draft with default values based on the selected product's type.
   * 3. Filters and applies default packaging options from the master package list.
   * 4. Clears any existing validation errors.
   * * @param {MasterProduct} product - The master product object selected by the user.
   * @returns {void}
   */
  const handleSelectProduct = (product: MasterProduct) => {
    setCurrentProductDetail(product);
    setProductDraft({
      productId: null,
      butcherProductId: product.id,
      name: product.name,
      price: product.price,
      fatPercentage: '',
      quantity:
        product.productType === ProductType.QUANTITY
          ? product.minQuantity
          : product.weight,
      instruction: '',
      addons: [],
      packages: [],
      notes: '',
      allowFatContentFlag: product.allowFatContentFlag,
    });

    // Set default package when product is selected
    const defaultPacks: OrderPackage[] = masterPackages
      .filter((p) => p.defaultFlag === 1)
      .map((p) => ({
        ...p,
        name: p.packageType,
        percentage: '100',
      }));
    setSelectedPackages(defaultPacks);
    setErrors({});
  };

  /**
   * Toggles the selection status of a product addon within the draft state.
   * * If the addon already exists in the draft, it is removed.
   * If it does not exist, it is appended to the addons list.
   * * @param {AddonsList} addon - The addon object to be added or removed.
   * @returns {void}
   */
  const toggleAddon = (addon: AddonsList) => {
    setProductDraft((prev) => {
      if (!prev) return prev;
      const exists = prev.addons?.some((a) => a.id === addon.id);
      return {
        ...prev,
        addons: exists
          ? prev.addons.filter((a) => a.id !== addon.id)
          : [...(prev.addons ?? []), addon],
      };
    });
  };

  /**
   * Handles selecting and adding a package to the selected packages list.
   *
   * This function creates a new `OrderPackage` object from the provided
   * `MasterPackage` and appends it to the `selectedPackages` state.
   *
   * Initial Percentage Logic:
   * - If this is the first package being added → assigns 100%
   * - If there are existing packages → assigns 0%
   *
   * @param pkg - The selected package data from the master list
   *
   */
  const handleSelectPackage = (pkg: MasterPackage) => {
    const newPkg: OrderPackage = {
      id: pkg.id,
      name: pkg.packageType,
      packageType: pkg.packageType,
      packageSize: pkg.packageSize,
      unit: pkg.unit,
      price: pkg.price,
      percentage: '50',
    };

    setSelectedPackages((prev) => {
      const updated = [...prev, newPkg];
      if (updated.length === 2) {
        return updated.map((p) => ({ ...p, percentage: '50' }));
      }
      return updated.map((p, i) => ({
        ...p,
        percentage: i === 0 ? '100' : '50',
      }));
    });

    setDropdownOpenIndex(null);
  };

  const handleDeletePackage = (packageId: number) => {
    setSelectedPackages((prev) => prev.filter((p) => p.id !== packageId));
  };

  /**
   * Handles updating the percentage value for a selected package.
   *
   * This function ensures that the entered percentage:
   * - Is a valid number (defaults to 0 if invalid)
   * - Stays within the range of 0 to 100
   *
   * It updates the selected package at the given index and, if exactly
   * two packages are present, automatically adjusts the other package
   * so that the total percentage always equals 100.
   *
   * @param index - The index of the package being updated
   * @param value - The input value representing the percentage (string format)
   *
   */
  const handlePercentageChange = (index: number, value: string) => {
    let num = Number(value);

    if (isNaN(num)) num = 0;

    num = Math.min(100, Math.max(0, num));

    setSelectedPackages((prev) => {
      const updated = [...prev];

      updated[index] = {
        ...updated[index],
        percentage: String(num),
      };

      // Adjust the other package
      if (updated.length === 2) {
        const otherIndex = index === 0 ? 1 : 0;

        updated[otherIndex] = {
          ...updated[otherIndex],
          percentage: String(100 - num),
        };
      }

      return updated;
    });
  };

  /**
   * Handles adding a product with validation.
   *
   * This function validates the current product draft based on business rules
   * such as required fields, minimum quantity, and fat content (if applicable).
   * If validation fails, corresponding error messages are set.
   * If validation succeeds, the product is passed to the `onAdd` callback
   * along with selected packages, and the modal/dialog is closed.
   *
   **/
  const handleAdd = () => {
    if (!currentProductDetail || !productDraft) return;
    const newErrors: Record<string, string> = {};

    if (
      currentProductDetail.allowFatContentFlag === 1 &&
      !productDraft.fatPercentage
    ) {
      newErrors.fatPercentage = errorMessage.FIELD_REQUIRED;
    }
    if (!productDraft.quantity || Number(productDraft.quantity) <= 0) {
      newErrors.quantity = errorMessage.FIELD_REQUIRED;
    }
    if (
      currentProductDetail.productType === ProductType.QUANTITY &&
      Number(productDraft.quantity) < currentProductDetail.minQuantity
    ) {
      newErrors.quantity = `Entered value should be at least ${currentProductDetail.minQuantity}`;
    }
    if (
      currentProductDetail.productType === ProductType.WEIGHT &&
      Number(productDraft.quantity) < currentProductDetail.weight
    ) {
      newErrors.quantity = `Entered value should be at least ${currentProductDetail.weight}`;
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    onAdd({
      ...productDraft,
      packages: selectedPackages,
    });
  };

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
      <div className="bg-white w-full max-w-2xl max-h-[95vh] overflow-y-auto rounded-lg shadow-lg p-3">
        {/* Header */}
        <div className="sticky top-0 z-10 bg-white flex justify-between items-center p-2 border-b">
          <div className="flex items-center gap-2">
            {currentProductDetail && (
              <button
                type="button"
                onClick={() => setCurrentProductDetail(null)}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M15 18l-6-6 6-6"
                    stroke="#000"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
            )}
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              className="shrink-0"
            >
              <path
                d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"
                fill="#2A6FBA"
              />
            </svg>
            <h2 className="text-xl font-medium">
              {currentProductDetail ? 'Product Detail' : 'Add New Product'}
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-600 hover:text-gray-900 text-lg leading-none"
          >
            ✕
          </button>
        </div>

        <div className="p-3">
          {currentProductDetail == null ? (
            <>
              {/* Search */}
              <div className="relative mb-3">
                <input
                  type="text"
                  value={productSearch}
                  onChange={(e) => setProductSearch(e.target.value)}
                  placeholder="Search..."
                  className="w-full border border-gray-200 rounded-md py-2 px-3 text-sm focus:outline-none focus:ring-1 focus:ring-primary"
                />
                <svg
                  className="absolute right-3 top-2.5 w-4 h-4 text-gray-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  />
                </svg>
              </div>

              <p className="text-sm text-gray-500 mb-2">All Products</p>

              {/* Product Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {filteredProducts.map((product) => {
                  const count = addedProducts.filter(
                    (item) =>
                      item.productId === product.id ||
                      item.butcherProductId === product.id,
                  ).length;
                  return (
                    <div key={product.id} className="flex flex-col gap-3">
                      <div className="relative p-4 rounded-lg border bg-[#FFFEFA] border-[#0000001A] shadow-md flex flex-col">
                        {count > 0 && (
                          <div className="absolute top-2 right-2 bg-primary text-white text-xs font-semibold w-6 h-6 rounded-full flex items-center justify-center shadow">
                            {count}
                          </div>
                        )}
                        <h3 className="font-semibold text-[#1A1C1E] text-lg">
                          {product.name}
                        </h3>
                        <p className="text-[#6C7689] text-sm mt-1">
                          {product.productType === ProductType.QUANTITY
                            ? `Min Quantity: ${product.minQuantity}`
                            : `Min Weight: ${product.weight} ${WeightType.LBS}`}
                        </p>
                        <div className="mt-auto font-semibold xl:text-xl text-[#1A1C1E] pt-4 ml-2">
                          ${product.price}/{WeightType.LBS}
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => {
                          handleSelectProduct(product);
                        }}
                        className="bg-primary text-white font-medium py-3 rounded-md"
                      >
                        Add
                      </button>
                    </div>
                  );
                })}
              </div>

              {/* Footer */}
              <div className="flex justify-end gap-4 mt-6">
                <button
                  type="button"
                  onClick={onClose}
                  className="text-primary font-bold"
                >
                  Discard
                </button>
                <button
                  type="button"
                  disabled={currentProductDetail == null}
                  className={`w-3/4 py-1.5 border border-primary font-semibold rounded-md 
                                    ${
                                      currentProductDetail == null
                                        ? 'text-gray-400 border-gray-300 bg-gray-100 cursor-not-allowed'
                                        : 'text-white bg-primary cursor-pointer'
                                    }`}
                >
                  Add to Cart
                </button>
              </div>
            </>
          ) : (
            <>
              {/* Product Detail Form */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-start">
                {/* LEFT — Product Info */}
                <div className="p-4 bg-[#F9F3F1] rounded-lg border border-gray-200">
                  <div className="flex justify-between items-start">
                    <div>
                      <h3 className="font-semibold text-[#1A1C1E]">
                        {currentProductDetail.name}
                      </h3>
                      <p className="text-[#555F71] text-sm mt-1">
                        {currentProductDetail.productType ===
                        ProductType.QUANTITY
                          ? `Min Quantity: ${currentProductDetail.minQuantity}`
                          : `Min Weight: ${currentProductDetail.weight} ${WeightType.LBS}`}
                      </p>
                    </div>
                    <div className="font-medium text-[#1A1C1E]">
                      ${currentProductDetail.price}/{WeightType.LBS}
                    </div>
                  </div>
                  {currentProductDetail.allowFatContentFlag === 1 && (
                    <div className="flex items-center gap-4 mt-4">
                      <span className="font-medium">Fat Percentage:</span>
                      <div>
                        <input
                          value={productDraft?.fatPercentage || ''}
                          onChange={(e) =>
                            setProductDraft((prev: any) => ({
                              ...prev,
                              fatPercentage: e.target.value,
                            }))
                          }
                          type="text"
                          className="w-full max-w-[160px] bg-white border border-gray-200 rounded py-1.5 pl-3 pr-8 font-bold text-lg shadow-sm outline-none focus:ring-1 focus:ring-primary"
                        />
                        {errors.fatPercentage && (
                          <p className="text-red-500 text-xs mt-1">
                            {errors.fatPercentage}
                          </p>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                {/* RIGHT — Measurement */}
                <div>
                  <h4 className="font-medium mb-3 text-lg">Measurement Type</h4>
                  <label className="flex items-center gap-2 mb-2">
                    {currentProductDetail.productType === ProductType.QUANTITY
                      ? 'Quantity'
                      : 'Weight'}
                  </label>
                  <input
                    type="number"
                    value={productDraft?.quantity || ''}
                    onChange={(e) =>
                      setProductDraft((prev: any) =>
                        prev
                          ? { ...prev, quantity: Number(e.target.value) }
                          : prev,
                      )
                    }
                    placeholder={
                      currentProductDetail.productType === ProductType.QUANTITY
                        ? 'Quantity'
                        : 'Weight'
                    }
                    className="w-full border rounded-md p-2 text-sm mb-2 shadow-sm outline-none focus:ring-1 focus:ring-primary"
                  />

                  {errors.quantity && (
                    <p className="text-red-500 text-xs mt-1">
                      {errors.quantity}
                    </p>
                  )}
                </div>
              </div>

              {/* Add-Ons */}
              <div className="mt-6">
                <div className="bg-[#FFFEFA] border border-[#0000001A] rounded-lg">
                  <div
                    className="flex items-center justify-between p-3 rounded-t-lg cursor-pointer"
                    onClick={() => setOpenAddons(!openAddons)}
                  >
                    <h3 className="font-bold text-[#333] text-lg">Add - Ons</h3>
                    <div className="flex items-center gap-2">
                      <div className="relative">
                        <input
                          type="text"
                          value={searchKey}
                          onChange={(e) => setSearchKey(e.target.value)}
                          placeholder="Search.."
                          className="border border-gray-200 rounded-md py-1 px-3 text-sm focus:outline-none w-40"
                        />
                        <svg
                          className="absolute right-2 top-2 w-4 h-4 text-gray-400"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                          />
                        </svg>
                      </div>
                      <svg
                        className="w-5 h-5 text-gray-500 cursor-pointer"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M19 9l-7 7-7-7"
                        />
                      </svg>
                    </div>
                  </div>
                  {openAddons && (
                    <div className="border border-t-0 bg-[#FFFEFA] border-[#0000001A] rounded-b-lg px-3 py-2 lg:px-6 lg:py-3">
                      <div className="grid grid-cols-2 gap-x-3 gap-y-0 lg:gap-x-10 lg:gap-y-1.5">
                        {filteredAddons.map((addon) => (
                          <label
                            key={addon.id}
                            className="flex items-center justify-between cursor-pointer py-1 min-w-0"
                          >
                            <span
                              className="text-[#333] font-medium text-sm truncate flex-1 min-w-0"
                              title={addon.name}
                            >
                              {addon.name}
                            </span>
                            <div className="flex items-center gap-1 xl:gap-3 shrink-0 ml-1">
                              <span className="text-[11px] md:text-xs lg:text-xs xl:text-base font-semibold text-gray-600 whitespace-nowrap">
                                +${addon.price}/Lbs
                              </span>
                              <input
                                type="checkbox"
                                className="w-4 h-4 accent-primary cursor-pointer shrink-0"
                                checked={productDraft?.addons?.some(
                                  (a: AddonsList) => a.id === addon.id,
                                )}
                                onChange={() => toggleAddon(addon)}
                              />
                            </div>
                          </label>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* Packaging */}
                <div className="mt-3">
                  <h4 className="font-semibold text-[#1C1B1F] mb-3 text-lg">
                    Packaging
                  </h4>
                  {selectedPackages.map((packaging, index) => (
                    <div
                      key={packaging.id}
                      className="relative flex gap-2 items-center mb-4"
                    >
                      <div className="flex-grow flex items-center justify-between p-3 border border-gray-200 rounded-lg bg-white">
                        <span className="text-gray-700 font-medium">
                          {packaging.packageType}
                        </span>
                        <div className="flex items-center gap-6">
                          <span className="text-gray-500 text-sm">
                            {packaging.packageSize} lbs
                          </span>
                          <div className="flex items-center gap-1 text-gray-600 font-medium">
                            <span>
                              {`+ $${packaging?.price?.toFixed(2)}`} /{' '}
                              {packaging?.unit}
                            </span>
                            <FontAwesomeIcon
                              icon={faChevronDown}
                              className="text-[#A09CAB] text-sm cursor-pointer"
                              onClick={() =>
                                setDropdownOpenIndex(
                                  dropdownOpenIndex === index ? null : index,
                                )
                              }
                            />
                          </div>
                        </div>
                      </div>
                      {selectedPackages.length > 1 && (
                        <div className="relative w-[80px] ml-2">
                          <input
                            type="number"
                            className="h-12 w-full pr-5 pl-2 border border-gray-300 rounded-md focus:outline-primary text-center"
                            value={packaging.percentage ?? ''}
                            onChange={(e) =>
                              handlePercentageChange(index, e.target.value)
                            }
                          />
                          <span className="absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none">
                            %
                          </span>
                        </div>
                      )}
                      <button
                        type="button"
                        className="p-3 border border-[#D4D4D4] rounded-lg text-[#4D545C]"
                        onClick={() => handleDeletePackage(packaging.id)}
                      >
                        <FontAwesomeIcon icon={faTrashCan} />
                      </button>
                      {dropdownOpenIndex === index &&
                        remainingPackages.length > 0 && (
                          <div className="absolute left-0 top-full mt-1 w-full bg-white border border-gray-200 rounded-lg shadow z-10">
                            <div className="grid grid-cols-3 gap-2 p-3 border-b text-xs font-semibold bg-[#F0F0F0] uppercase">
                              <span>Title</span>
                              <span>Size in lbs</span>
                              <span>Upcharge</span>
                            </div>
                            {remainingPackages.map((pack) => (
                              <div
                                key={pack.id}
                                onClick={() => handleSelectPackage(pack)}
                                className="grid grid-cols-3 gap-2 p-3 hover:bg-gray-100 cursor-pointer text-sm"
                              >
                                <span className="text-gray-700">
                                  {pack.packageType}
                                </span>
                                <span className="text-gray-500">
                                  {pack.packageSize} lbs
                                </span>
                                <span className="text-gray-600">{`+ $${pack.price.toFixed(2)} / ${pack.unit}`}</span>
                              </div>
                            ))}
                          </div>
                        )}
                    </div>
                  ))}
                  {selectedPackages.length < 2 && (
                    <button
                      type="button"
                      onClick={() => setDropdownOpenIndex(0)}
                      className="w-full py-2.5 border border-primary text-primary font-bold rounded-lg"
                    >
                      Add Packaging
                    </button>
                  )}
                </div>
              </div>

              {/* Notes */}
              <div className="mt-3">
                <h4 className="font-medium mb-2 text-lg">Notes</h4>
                <textarea
                  value={productDraft?.notes || ''}
                  onChange={(e) =>
                    setProductDraft((prev: any) =>
                      prev ? { ...prev, notes: e.target.value } : prev,
                    )
                  }
                  placeholder="Add notes or description..."
                  className="w-full border rounded-md p-3"
                />
              </div>

              {/* Footer */}
              <div className="flex justify-end gap-4 mt-6">
                <button
                  type="button"
                  onClick={() => setCurrentProductDetail(null)}
                  className="text-primary font-bold"
                >
                  Discard & Go back
                </button>
                <button
                  type="button"
                  onClick={handleAdd}
                  className="w-3/4 py-1.5 border border-primary text-white bg-primary font-semibold rounded-lg mt-3"
                >
                  Add to Cart
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};
